import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { trpc } from '../lib/trpc';
import { PageContainer } from '../components/PageContainer';
import { Button } from '../components/ui/Button';
import {
  PriceRangeSelector,
  PrimaryAtmosphereSelector,
  AmenitiesSelector,
  ReservationPolicySelector,
} from '../components/VenueForm';
import { VenueFormErrorBoundary } from '../components/VenueForm/VenueFormErrorBoundary';
import { useVenueFormValidation } from '../hooks/useVenueFormValidation';
import { formatTrpcError } from '../utils/formatTrpcError';
import type {
  PriceRange,
  PrimaryAtmosphere,
  Amenity,
  VenueStatus,
  ReservationPolicy,
} from '@wineryxplorer/shared';

import { createDefaultOperatingHours } from './VenueCreate/constants';
import type { OperatingHours, VenueType } from './VenueCreate/constants';
import { BasicInformationSection } from './VenueCreate/BasicInformationSection';
import { AddressSection } from './VenueCreate/AddressSection';
import { ContactInformationSection } from './VenueCreate/ContactInformationSection';
import { OperatingHoursSection } from './VenueCreate/OperatingHoursSection';
import { FoodServiceSection } from './VenueCreate/FoodServiceSection';
import { FormSection } from '../components/ui/FormSection';

export const VenueCreate = () => {
  const navigate = useNavigate();
  const utils = trpc.useContext();
  const validation = useVenueFormValidation();

  // Form state - Basic Information
  const [name, setName] = useState('');
  const [venueType, setVenueType] = useState<VenueType>('winery');
  const [status] = useState<VenueStatus>('published');
  const [description, setDescription] = useState('');

  // Form state - Address
  const [street, setStreet] = useState('');
  const [city, setCity] = useState('');
  const [state, setState] = useState('');
  const [zipCode, setZipCode] = useState('');
  const [country, setCountry] = useState('United States');

  // Form state - Contact Information
  const [phone, setPhone] = useState('');
  const [email, setEmail] = useState('');
  const [website, setWebsite] = useState('');

  // Form state - Operating Hours
  const [operatingHours, setOperatingHours] = useState<OperatingHours>(createDefaultOperatingHours);
  const [operatingHoursNotes, setOperatingHoursNotes] = useState('');

  // Form state - Food Service
  const [foodAvailableForPurchase, setFoodAvailableForPurchase] = useState(false);
  const [foodDescription, setFoodDescription] = useState('');

  // Form state - Enhancements
  const [priceRange, setPriceRange] = useState<PriceRange | undefined>(undefined);
  const [primaryAtmosphere, setPrimaryAtmosphere] = useState<PrimaryAtmosphere | undefined>(undefined);
  const [amenities, setAmenities] = useState<Amenity[]>([]);
  const [reservationPolicy, setReservationPolicy] = useState<ReservationPolicy>('recommended');

  // Error state
  const [submitError, setSubmitError] = useState('');

  // Mutation
  const createVenue = trpc.venue.create.useMutation({
    onSuccess: (data) => {
      utils.venue.list.invalidate();
      validation.clearErrors();
      // Navigate to the new venue's detail page
      navigate(`/wineries/${data._id}`);
    },
    onError: (error: unknown) => {
      const formattedError = formatTrpcError(error);
      setSubmitError(formattedError);
    },
  });

  const handleOperatingHoursChange = (
    day: string,
    field: 'open' | 'close' | 'closed',
    value: string | boolean
  ) => {
    setOperatingHours((prev) => ({
      ...prev,
      [day]: {
        ...prev[day],
        [field]: value,
      },
    }));
  };

  const handleSubmit = async () => {
    setSubmitError('');

    // Validate enhancement fields
    validation.validateAll({
      priceRange,
      primaryAtmosphere,
      amenities,
    });

    // If there are validation errors, don't submit
    if (!validation.isValid) {
      setSubmitError('Please fix the validation errors before submitting.');
      return;
    }

    // Filter out incomplete operating hours entries
    const validOperatingHours: OperatingHours = {};
    Object.entries(operatingHours).forEach(([day, hours]) => {
      if (hours.closed || (hours.open && hours.close)) {
        validOperatingHours[day] = hours;
      }
    });

    try {
      await createVenue.mutateAsync({
        name,
        venueType,
        status,
        description: description || undefined,
        address: {
          street,
          city,
          state,
          zipCode,
          country,
        },
        phone: phone || undefined,
        email: email || undefined,
        website: website || undefined,
        operatingHours: validOperatingHours,
        operatingHoursNotes: operatingHoursNotes || undefined,
        foodAvailableForPurchase,
        foodDescription: foodDescription || undefined,
        priceRange,
        primaryAtmosphere,
        amenities,
        reservationPolicy,
        featured: undefined,
        logoImageId: undefined,
        heroImageIds: [],
        imageIds: [],
      });
    } catch (error) {
      // Error handled by onError callback
      console.error('Failed to create venue:', error);
    }
  };

  const handleCancel = () => {
    navigate('/admin/dashboard');
  };

  return (
    <VenueFormErrorBoundary>
      <div className="px-0 md:px-4 pt-2 pb-4 max-w-4xl mx-auto">
        <PageContainer className="mt-2">
          <div className="pb-6">
            <h1 className="text-3xl font-bold text-gray-900 mb-6">Create New Venue</h1>

            {/* Action Buttons */}
            <div className="flex gap-4 mb-6 justify-end">
              <Button
                variant="primary"
                onClick={handleSubmit}
                disabled={createVenue.isPending}
                loading={createVenue.isPending}
              >
                {createVenue.isPending ? 'Creating...' : 'Create Venue'}
              </Button>
              <Button variant="outline" onClick={handleCancel}>
                Cancel
              </Button>
            </div>

            {/* Validation errors display */}
            {submitError && (
              <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-md">
                <p className="text-sm text-red-600">{submitError}</p>
              </div>
            )}
          </div>

          {/* Form content */}
          <div className="pt-6">
            <BasicInformationSection
              name={name}
              onNameChange={setName}
              venueType={venueType}
              onVenueTypeChange={setVenueType}
              description={description}
              onDescriptionChange={setDescription}
            />

            <AddressSection
              street={street}
              onStreetChange={setStreet}
              city={city}
              onCityChange={setCity}
              state={state}
              onStateChange={setState}
              zipCode={zipCode}
              onZipCodeChange={setZipCode}
              country={country}
              onCountryChange={setCountry}
            />

            <ContactInformationSection
              phone={phone}
              onPhoneChange={setPhone}
              email={email}
              onEmailChange={setEmail}
              website={website}
              onWebsiteChange={setWebsite}
            />

            <OperatingHoursSection
              operatingHours={operatingHours}
              onOperatingHoursChange={handleOperatingHoursChange}
              operatingHoursNotes={operatingHoursNotes}
              onOperatingHoursNotesChange={setOperatingHoursNotes}
            />

            <FoodServiceSection
              foodAvailableForPurchase={foodAvailableForPurchase}
              onFoodAvailableForPurchaseChange={setFoodAvailableForPurchase}
              foodDescription={foodDescription}
              onFoodDescriptionChange={setFoodDescription}
            />

            {/* Enhancement Selectors */}
            <FormSection title="Additional Details" contentClassName="space-y-6">
              <PriceRangeSelector value={priceRange} onChange={setPriceRange} error={validation.errors.priceRange} />
              <PrimaryAtmosphereSelector
                value={primaryAtmosphere}
                onChange={setPrimaryAtmosphere}
                error={validation.errors.primaryAtmosphere}
              />
              <AmenitiesSelector value={amenities} onChange={setAmenities} error={validation.errors.amenities} />
              <ReservationPolicySelector value={reservationPolicy} onChange={setReservationPolicy} />
            </FormSection>
          </div>
        </PageContainer>
      </div>
    </VenueFormErrorBoundary>
  );
};
